import { EGameStatus } from '@lbs/shared-rp-types'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import { Button, Chip } from '@mui/material'
import { MaterialReactTable, MRT_ColumnDef, MRT_PaginationState, useMaterialReactTable } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { queryKeys } from '../../constants/EQueryKeysConstant'
import mrtLocalization from '../../constants/mrtLocalization'
import { ERole } from '../../constants/role'
import { Prp2boxApiGames } from '../../prp2boxApi'
import { RpGame } from '../../prp2boxApi/games'
import routes from '../../router/routes'
import { useStore } from '../../stores'
import { EDrawerView } from '../../stores/DrawerCRUD.store'
import { formatToMoscowTZ } from '../../utils/date'

const RpGamesTable: FC = observer(() => {
  const navigate = useNavigate()
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 10 })
  const { drawerCRUDStore, loginStore } = useStore()

  const queryParams = {
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
  }

  const { data, isError, isFetching, isLoading } = useQuery(
    queryKeys.rpGames.allWithParams(queryParams),
    () =>
      Prp2boxApiGames.getAll({
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      }),
    {
      refetchOnWindowFocus: false,
      onError: (err) => toast.error(`${err}`),
      enabled: true,
      keepPreviousData: true,
      ...(loginStore.role === ERole.DEALER && {
        select: ({ totalRows, rows }) => {
          return {
            totalRows,
            rows: rows.filter((item) => item.dealer.login === loginStore.operator?.login),
          }
        },
      }),
    }
  )

  const columns: MRT_ColumnDef<RpGame>[] = [
    {
      accessorKey: 'name',
      header: 'Имя',
      Cell: ({ cell, row }) => {
        const label = cell.getValue<string>()
        return (
          <Chip
            color="info"
            variant="outlined"
            icon={<SportsEsportsIcon />}
            label={label}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()

              const { id } = row.original

              if (!id) return

              drawerCRUDStore.changeView(EDrawerView.RP_GAME_EDIT, String(id))
            }}
          />
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Статус',
      Cell: ({ cell }) => {
        const value = cell.getValue<EGameStatus>()

        const translations = {
          [EGameStatus.CREATED]: 'Создана',
          [EGameStatus.ACTIVE]: 'В процессе',
          [EGameStatus.FINISHED]: 'Завершена',
          [EGameStatus.DELETED]: 'Удалена',
        }

        const translationValue = translations[value] || value
        let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
        switch (value) {
          case EGameStatus.ACTIVE:
            color = 'success'
            break
          case EGameStatus.CREATED:
            color = 'warning'
            break
          default:
            color = 'default'
        }
        return <Chip color={color} label={translationValue} />
      },
    },
    {
      accessorKey: 'player.login',
      header: 'Игрок',
    },
    {
      accessorKey: 'limit.name',
      header: 'Лимит',
    },
    {
      accessorKey: 'bonus.name',
      header: 'Бонус',
    },
    {
      accessorKey: 'stand.name',
      header: 'Стойка',
    },
    {
      accessorKey: 'payout.name',
      header: 'Выплаты',
    },
    {
      accessorKey: 'dealer.login',
      header: 'Дилер',
    },
    {
      accessorKey: 'updatedAt',
      header: 'Последнее обновление',
      sortingFn: 'datetime',
      Cell: ({ cell }) => {
        return formatToMoscowTZ(cell.getValue<string>())
      },
    },
  ]

  const table = useMaterialReactTable({
    ...mrtLocalization,
    enableGlobalFilter: false,
    data: data?.rows || [],
    columns,
    rowCount: data?.totalRows || 0,
    state: {
      pagination,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
    manualPagination: true,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        event.preventDefault()
        event.stopPropagation()

        if (loginStore.isDealer) {
          navigate(`${routes.manageGameRussianPoker}/${row.original.publicId}`)
        } else {
          navigate(`${routes.prpSessionsById.replace(':gameId', String(row.original.id))}`)
        }
      },
      sx: {
        cursor: 'pointer',
      },
    }),
    renderTopToolbarCustomActions: () => {
      return (
        <Button
          variant="contained"
          onClick={() => {
            drawerCRUDStore.changeView(EDrawerView.RP_GAME_CREATE)
          }}>
          Создать новую игру
        </Button>
      )
    },
  })

  return <MaterialReactTable table={table} />
})

export default RpGamesTable
